import styled, { css } from 'styled-components';
import {
  StickyHeaderModal,
  MarkdownWrapper,
  Icon,
  ModalTitleContainer,
  ModalTitleMain,
  ModalTitleCaption,
} from '../common';
import { TestQuestionRow } from './StepQuestionsResultsTable';
import {
  AssessmentTestStep,
  AssessmentTestResult,
} from '../../../../api/admin';
import { typeLabels } from './QuestionTypeCell';
import { renderSectionAnswer } from './StepModalComponents';

interface QuestionAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  question: TestQuestionRow | null;
  step: AssessmentTestStep;
  stepReview: AssessmentTestResult['scenarioEvaluations'][number] | null;
}

export default function QuestionAnalysisModal({
  isOpen,
  onClose,
  question,
  step,
  stepReview,
}: QuestionAnalysisModalProps) {
  if (!isOpen || !question) {
    return null;
  }

  const { section, answer, questionNumber, stepObjectives } = question;
  const typeLabel = typeLabels[section.type] || section.type;

  // Find objectives related to this question
  const relatedObjectives = stepObjectives
    .filter((objective) =>
      objective.facets.some((facet) => facet.questions.includes(section.id))
    )
    .map((objective) => {
      const objectiveEvaluation = stepReview?.objectiveEvaluations.find(
        (evaluation) => evaluation.objectiveId === objective.id
      );
      return {
        ...objective,
        evaluation: objectiveEvaluation,
      };
    })
    .filter((obj) => obj.evaluation); // Only show objectives that have evaluations

  return (
    <StickyHeaderModal
      title={
        <ModalTitleContainer>
          <ModalTitleMain>
            {step.label} - Question {questionNumber}
          </ModalTitleMain>
          <ModalTitleCaption>{typeLabel}</ModalTitleCaption>
        </ModalTitleContainer>
      }
      onClose={onClose}
    >
      <Content>
        {/* Question Section */}
        <Section>
          <SectionTitle>Question</SectionTitle>
          <QuestionContent>
            <MarkdownWrapper md={section.content ?? ''} />
          </QuestionContent>
        </Section>

        {/* Answer Section */}
        <Section>
          <SectionTitle>Answer</SectionTitle>
          <AnswerContent>
            {answer ? (
              renderSectionAnswer(answer, section)
            ) : (
              <EmptyState>No answer provided</EmptyState>
            )}
          </AnswerContent>
        </Section>

        {/* Follow-up if exists */}
        {section.followUpContent && (
          <>
            <Section>
              <SectionTitle>Follow-up Question</SectionTitle>
              <QuestionContent>
                <MarkdownWrapper md={section.followUpContent} />
              </QuestionContent>
            </Section>
            <Section>
              <SectionTitle>Follow-up Answer</SectionTitle>
              <AnswerContent>
                {answer?.followUpAnswer ? (
                  <MarkdownWrapper md={answer.followUpAnswer} />
                ) : (
                  <EmptyState>No follow-up answer provided</EmptyState>
                )}
              </AnswerContent>
            </Section>
          </>
        )}

        {/* Objectives Review Section - Only for non-self-assessment */}
        {relatedObjectives.length > 0 && (
          <>
            <Divider />
            <Section>
              <Row
                style={{
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px',
                }}
              >
                <Icon className="fa-regular fa-sparkles" />
                <SectionTitle style={{ margin: 0 }}>
                  Objectives Review
                </SectionTitle>
              </Row>
              <ObjectivesListContainer>
                {relatedObjectives.map((objective) => (
                  <ObjectiveListItem key={objective.id}>
                    <ObjectiveItemHeader>
                      <ObjectiveIdText>{objective.id}</ObjectiveIdText>
                      <ObjectiveBadgesRow>
                        <ObjectiveScoreBadge>
                          {objective.evaluation?.score}/10
                        </ObjectiveScoreBadge>
                        <ObjectiveStatusBadge
                          status={objective.evaluation?.status}
                        >
                          {objective.evaluation?.status}
                        </ObjectiveStatusBadge>
                      </ObjectiveBadgesRow>
                    </ObjectiveItemHeader>
                    <ObjectiveItemDescription>
                      {objective.evaluation?.objectiveDescription}
                    </ObjectiveItemDescription>
                    <ObjectiveEvidenceSection>
                      <ObjectiveEvidenceTitle>
                        Evidence Analysis
                      </ObjectiveEvidenceTitle>
                      <MarkdownWrapper
                        md={objective.evaluation?.evidenceAnalysis ?? ''}
                      />
                    </ObjectiveEvidenceSection>
                  </ObjectiveListItem>
                ))}
              </ObjectivesListContainer>
            </Section>
          </>
        )}
      </Content>
    </StickyHeaderModal>
  );
}

// Styled Components

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 16px 0 32px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.div`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.3px;
`;

const QuestionContent = styled.div`
  /* padding: 16px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  border: 1px solid ${({ theme }) => theme.colors.bgActive}; */
`;

const AnswerContent = styled.div`
  /* Styling handled by renderSectionAnswer components */
`;

const EmptyState = styled.div`
  padding: 24px;
  text-align: center;
  color: ${({ theme }) => theme.colors.textPlaceholder};
  font-style: italic;
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  border-radius: 8px;
  border: 1px dashed ${({ theme }) => theme.colors.bgActive};
`;

const Divider = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.colors.bgActive};
  margin: 16px 0;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

// New objectives list styling - inspired by ResultSkillBreakdownModal
const ObjectivesListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  border-radius: 8px;
  overflow: hidden;
`;

const ObjectiveListItem = styled.div`
  padding: 16px 20px;
  background-color: ${({ theme }) => theme.colors.bgPage2};
  border-left: 3px solid ${({ theme }) => theme.colors.bgActive};
  border-bottom: 1px solid ${({ theme }) => theme.colors.bgActive};
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: all 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    border-left-color: ${({ theme }) => theme.colors.textActive};
    background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  }
`;

const ObjectiveItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const ObjectiveIdText = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textRegular};
  font-family: 'Courier New', monospace;
  letter-spacing: -0.2px;
  opacity: 0.7;
`;

const ObjectiveBadgesRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ObjectiveScoreBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  background-color: ${({ theme }) => `${theme.colors.buttonPrimary}20`};
  border: 1px solid ${({ theme }) => theme.colors.buttonPrimary};
  color: ${({ theme }) => theme.colors.buttonPrimary};
`;

const ObjectiveStatusBadge = styled.div<{ status?: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;

  ${({ status, theme }) => {
    switch (status) {
      case 'exceeded':
        return css`
          background-color: #1dea9920;
          border: 1px solid #1dea99;
          color: #1dea99;
        `;
      case 'met':
        return css`
          background-color: #1dea9920;
          border: 1px solid #1dea9980;
          color: #1dea99;
        `;
      case 'partial':
        return css`
          background-color: ${theme.colors.buttonSecondary}20;
          border: 1px solid ${theme.colors.buttonSecondary};
          color: ${theme.colors.buttonSecondary};
        `;
      case 'not_met':
        return css`
          background-color: ${theme.colors.buttonTertiary}20;
          border: 1px solid ${theme.colors.buttonTertiary};
          color: ${theme.colors.buttonTertiary};
        `;
      default:
        return css`
          background-color: ${theme.colors.textRegular}15;
          border: 1px solid ${theme.colors.textRegular}60;
          color: ${theme.colors.textRegular};
        `;
    }
  }}
`;

const ObjectiveItemDescription = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.5;
  font-weight: 500;
`;

const ObjectiveEvidenceSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
`;

const ObjectiveEvidenceTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textActive};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;
